import { Fragment, useEffect, useRef, type ReactNode } from 'react';
import { keyLabel, keysOf, useShortcutKeys } from './shortcuts.ts';

/**
 * Help in every view (docs/PARITY-STEP-1-PLAN.md section 6): the web's own pages,
 * docs/help/web/*.md, in the desktop's five sections, built into the app. They're turned
 * into React elements here, never into HTML, so nothing in them is parsed as markup. As on
 * the desktop, `[Ctrl+S](key:writing.save)` shows the user's own keys for the action, and
 * `[...](topic:entry)` opens another page.
 */

const PAGES = import.meta.glob('../../../docs/help/web/*.md', { query: '?raw', import: 'default', eager: true }) as Record<string, string>;

/** The sections every page has, in order: the desktop's `HelpPages.SECTIONS`. */
export const SECTIONS = ["What it's for", "What's on screen", 'Shortcuts', 'Try it in the sample realm', 'More'] as const;

/** Each view's page. */
export const TOPICS = ['realms', 'realm', 'entry', 'search', 'knowledge-graph', 'settings', 'account', 'dice-roller', 'random-tables', 'character-sheets', 'generators', 'chronology', 'calendar', 'story-studio', 'focus-mode', 'consistency-check', 'game-master', 'knowledge-modes', 'idea-board', 'atlas', 'icon-library', 'thesaurus', 'spelling', 'sharing'] as const;
export type Topic = (typeof TOPICS)[number] | 'quickstart';

export function pageSource(topic: string): string | null {
  return Object.entries(PAGES).find(([path]) => path.endsWith(`/${topic}.md`))?.[1] ?? null;
}

/** The page for where the app is. */
export function topicFor(pathname: string): Topic {
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/account')) return 'account';
  const realm = /^\/realm\/[^/]+(\/.*)?$/.exec(pathname);
  if (realm) {
    const rest = realm[1] ?? '';
    if (rest.startsWith('/search')) return 'search';
    if (rest.startsWith('/sharing')) return 'sharing';
    if (rest.startsWith('/graph')) return 'knowledge-graph';
    if (rest.startsWith('/chronology')) return 'chronology';
    if (rest.startsWith('/calendars')) return 'calendar';
    if (rest.startsWith('/story')) return 'story-studio';
    if (rest.startsWith('/consistency')) return 'consistency-check';
    if (rest.startsWith('/campaigns')) return 'game-master';
    if (rest.startsWith('/modes')) return 'knowledge-modes';
    if (rest.startsWith('/boards')) return 'idea-board';
    if (rest.startsWith('/atlas/icons')) return 'icon-library';
    if (rest.startsWith('/atlas')) return 'atlas';
    if (rest.startsWith('/tables')) return 'random-tables';
    if (rest.startsWith('/sheets') || rest.startsWith('/templates')) return 'character-sheets';
    if (rest.startsWith('/generators') || rest.startsWith('/scripts')) return 'generators';
    if (rest.startsWith('/entry') || rest.startsWith('/new')) return 'entry';
    return 'realm';
  }
  return 'realms';
}

// ---- Markdown, the subset the help pages and release notes use ----

type Block =
  | { kind: 'heading'; level: number; text: string }
  | { kind: 'paragraph'; text: string }
  | { kind: 'list'; ordered: boolean; items: { text: string; children: string[] }[] }
  | { kind: 'table'; head: string[]; rows: string[][] };

const cells = (line: string) => line.trim().replace(/^\|/, '').replace(/\|$/, '').split('|').map((c) => c.trim());

export function parseBlocks(source: string): Block[] {
  const blocks: Block[] = [];
  const lines = source.replace(/\r\n?/g, '\n').split('\n');
  let paragraph: string[] = [];
  let list: Extract<Block, { kind: 'list' }> | null = null;
  let table: Extract<Block, { kind: 'table' }> | null = null;
  const endTable = () => {
    if (table) blocks.push(table);
    table = null;
  };
  const endParagraph = () => {
    if (paragraph.length) blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
    paragraph = [];
  };
  const endList = () => {
    if (list) blocks.push(list);
    list = null;
  };
  for (const line of lines) {
    if (line.trim().startsWith('|')) {
      endParagraph();
      endList();
      // The row under the head, `| --- | --- |`, only marks it.
      if (/^\s*\|[\s:|-]+\|\s*$/.test(line)) continue;
      if (table) table.rows.push(cells(line));
      else table = { kind: 'table', head: cells(line), rows: [] };
      continue;
    }
    endTable();
    const heading = /^(#{1,6}) (.*)$/.exec(line);
    const item = /^(- |\d+\. )(.*)$/.exec(line);
    const child = /^  - (.*)$/.exec(line);
    const more = /^ {2,}(\S.*)$/.exec(line);
    if (line.trim() === '') {
      endParagraph();
      endList();
    } else if (heading) {
      endParagraph();
      endList();
      blocks.push({ kind: 'heading', level: heading[1]!.length, text: heading[2]! });
    } else if (item) {
      endParagraph();
      const ordered = item[1] !== '- ';
      if (!list || list.ordered !== ordered) {
        endList();
        list = { kind: 'list', ordered, items: [] };
      }
      list.items.push({ text: item[2]!, children: [] });
    } else if (child && list) {
      list.items.at(-1)!.children.push(child[1]!);
    } else if (more && list) {
      const last = list.items.at(-1)!;
      if (last.children.length) last.children[last.children.length - 1] += ` ${more[1]}`;
      else last.text += ` ${more[1]}`;
    } else {
      endList();
      paragraph.push(line.trim());
    }
  }
  endParagraph();
  endList();
  endTable();
  return blocks;
}

const INLINE = /\*\*(.+?)\*\*|`([^`]+)`|\[([^\]]+)\]\(([^)\s]+)\)|_([^_]+)_|\*([^*\s][^*]*)\*/g;

function Inline({ text, onTopic }: { text: string; onTopic: (topic: Topic) => void }) {
  useShortcutKeys();
  const out: ReactNode[] = [];
  let at = 0;
  for (const match of text.matchAll(INLINE)) {
    out.push(text.slice(at, match.index));
    const [, bold, code, label, href, underscored, starred] = match;
    const italic = underscored ?? starred;
    const key = out.length;
    if (bold !== undefined) out.push(<strong key={key}><Inline text={bold} onTopic={onTopic} /></strong>);
    else if (code !== undefined) out.push(<code key={key}>{code}</code>);
    else if (italic !== undefined) out.push(<em key={key}>{italic}</em>);
    else if (href!.startsWith('key:')) {
      const keys = keysOf(href!.slice(4));
      out.push(
        <kbd key={key} title={keys.length ? undefined : 'No key: set one in Settings'}>
          {keys.length ? keys.map(keyLabel).join(' or ') : '(no key)'}
        </kbd>,
      );
    } else if (href!.startsWith('topic:')) {
      const topic = href!.slice(6) as Topic;
      out.push(
        <button key={key} type="button" className="link-button" onClick={() => onTopic(topic)}>
          {label}
        </button>,
      );
    } else if (/^https:\/\//.test(href!)) {
      out.push(
        <a key={key} href={href} target="_blank" rel="noopener noreferrer">
          {label}
        </a>,
      );
    } else out.push(label);
    at = match.index + match[0].length;
  }
  out.push(text.slice(at));
  return <>{out}</>;
}

export function HelpPage({ topic, onTopic }: { topic: Topic; onTopic: (topic: Topic) => void }) {
  const source = pageSource(topic);
  if (!source) return <p>There's no help for this view yet.</p>;
  return <Markdown source={source} onTopic={onTopic} />;
}

/**
 * Markdown as React elements, never as HTML. `shift` moves headings down, so a document's
 * own `#` can sit under a page's heading (the download page's release notes).
 */
export function Markdown({ source, onTopic = () => undefined, shift = 0 }: { source: string; onTopic?: (topic: Topic) => void; shift?: number }) {
  return (
    <>
      {parseBlocks(source).map((block, i) => {
        if (block.kind === 'table') {
          return (
            <div key={i} className="table-scroll">
              <table>
                <thead>
                  <tr>
                    {block.head.map((cell, j) => (
                      <th key={j} scope="col">
                        <Inline text={cell} onTopic={onTopic} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {block.rows.map((row, j) => (
                    <tr key={j}>
                      {row.map((cell, k) => (
                        <td key={k}>
                          <Inline text={cell} onTopic={onTopic} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        if (block.kind === 'heading') {
          const Tag = (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'] as const)[Math.min(5, block.level - 1 + shift)]!;
          return (
            <Tag key={i}>
              <Inline text={block.text} onTopic={onTopic} />
            </Tag>
          );
        }
        if (block.kind === 'paragraph') {
          return (
            <p key={i}>
              <Inline text={block.text} onTopic={onTopic} />
            </p>
          );
        }
        const List = block.ordered ? 'ol' : 'ul';
        return (
          <List key={i}>
            {block.items.map((item, j) => (
              <li key={j}>
                <Inline text={item.text} onTopic={onTopic} />
                {item.children.length > 0 && (
                  <ul>
                    {item.children.map((c, k) => (
                      <Fragment key={k}>
                        <li>
                          <Inline text={c} onTopic={onTopic} />
                        </li>
                      </Fragment>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </List>
        );
      })}
    </>
  );
}

/** The Quickstart Guide, as a page of its own (the welcome page links it). */
export function QuickstartPage() {
  return (
    <article className="document help-document">
      <HelpPage topic="quickstart" onTopic={() => undefined} />
    </article>
  );
}

/** The help beside the page, as the desktop's help panel: Esc or Close puts it away. */
export function HelpPanel({ topic, onTopic, onClose }: { topic: Topic; onTopic: (topic: Topic) => void; onClose: () => void }) {
  const panel = useRef<HTMLElement>(null);
  useEffect(() => {
    panel.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, topic]);
  return (
    <aside className="help-panel" aria-label="Help" tabIndex={-1} ref={panel}>
      <div className="help-panel-head">
        <button type="button" className="link-button" onClick={() => onTopic('quickstart')}>
          Quickstart Guide
        </button>
        <button type="button" onClick={onClose}>
          Close
        </button>
      </div>
      <HelpPage topic={topic} onTopic={onTopic} />
    </aside>
  );
}
